import { createContext, useContext } from 'react'
import { create } from 'zustand'

// Registry of currently-mounted web modal DOM nodes, used as Radix Portal
// containers for DropdownMenu content so dropdowns render above modal overlays.
//
// Modals register their inner [aria-modal] View node on mount and unregister it
// on unmount. Unregistration happens synchronously during React's commit
// (ref callback null + unmount effect) — BEFORE Reanimated's web exit
// animation lingers the DOM node. This avoids the old
// `document.querySelectorAll('[aria-modal="true"]')` lookup, which returned
// stale/dying modal nodes after navigating away from a modal and corrupted
// every DropdownMenu's Radix portal (dropdowns stopped opening on the new page).
const useModalPortalStore = create((set) => ({
    stack: [],
    register: (node) => set((s) => (s.stack.includes(node) ? s : { stack: [...s.stack, node] })),
    unregister: (node) => set((s) => ({ stack: s.stack.filter((n) => n !== node) })),
}))

// Per-modal portal target. Each web modal provides its portal-safe host node
// (an ancestor that is NOT a fixed-position containing block and does not clip
// — SheetModal/ModalWrapper provide their outer backdrop container, since the
// registered panel carries a Reanimated transform and overflow:hidden that
// would misplace and clip fixed-position Radix popovers).
//
// Scoping the container through React context (which propagates through RN
// Modal portals on web) fixes stacked modals for any depth: a dropdown in the
// TOP modal portals into that modal's own subtree — above its backdrop and
// above every lower modal — while a dropdown in a LOWER modal portals into
// its own modal and correctly stays below the upper one. With a single global
// top-of-stack container, a lower modal's dropdown rendered inside the upper
// modal, and a SheetModal-on-top dropdown was clipped/misplaced entirely.
export const ModalPortalContext = createContext(null)

// Selector: the enclosing modal's portal host when inside a modal subtree;
// otherwise the top-of-stack registered modal node (last mounted wins), or null.
// Subscribing to `stack` makes consumers re-render when a modal mounts/unmounts.
export const useModalPortalContainer = () => {
    const scopedNode = useContext(ModalPortalContext)
    const stack = useModalPortalStore((s) => s.stack)
    return scopedNode ?? (stack.length ? stack[stack.length - 1] : null)
}

export default useModalPortalStore
